/* RoLink Biotechnology – translation dictionary (EN / HU).
   Dynamic content (team bios, news articles, partner names) is served
   from MongoDB and stays as-seeded; only static UI strings are localised. */

export const translations = {
  en: {
    nav: {
      home: "Home",
      technology: "Technology",
      science: "Science",
      applications: "Applications",
      partners: "Partners",
      team: "Team",
      investors: "Investors",
      news: "News",
      contact: "Contact",
      search: "Search",
    },
    hero: {
      badge: "Pandemic Preparedness Platform",
      headlineLine1: "ART OF",
      headlineLine2Pre: "THE ",
      headlineLine2Red: "KILL",
      subtitle: "Rapid-response UV inactivation for pandemic preparedness.",
      body: "RoLink Biotechnology develops UV-based viral inactivation platforms enabling safer diagnostics, vaccine development, and biodefense applications while preserving viral structure.",
      explore: "Explore Technology",
      investor: "Investor Information",
      contact: "Contact Us",
      scroll: "Scroll",
    },
    problem: {
      badge: "The Global Challenge",
      headlinePre: "Only ",
      headlineAccent: "51 BSL-4 facilities",
      headlineSuf: " exist worldwide.",
      body: "High-containment virology research is expensive, operationally restrictive, and inaccessible to most institutions. Dangerous pathogens require BSL-3/BSL-4 infrastructure that fewer than 1% of laboratories can provide — creating a critical bottleneck in pandemic preparedness, vaccine development, and biodefense research.",
      bsl: { l2: "Standard labs", l3: "Enhanced containment", l4: "Maximum containment" },
      cta: "RoLink eliminates this bottleneck",
    },
    technology: {
      label: "UV Inactivation Platform",
      heading: "How It Works",
      stages: {
        activeT: "Active Virus",
        activeD: "Dangerous live virus requiring BSL-3/BSL-4 containment. Restricted access, low R&D capacity, highly expensive and highly skilled workforce required. Only 51 BSL-4 facilities globally.",
        uvT: "UV-C Inactivation (254nm)",
        uvD: "Proprietary UV-C inactivation at 254nm wavelength. Precisely calibrated exposure (5–90s) destroys viral replication while preserving structural integrity.",
        inT: "UV Inactivated Virus",
        inD: "Safe, structurally intact virus: 100% protein preservation (ELISA validated), 100% PCR-detectable RNA (qPCR, R²=0.007). Operable at BSL-2. No biohazard waste. Cost-efficient, accessible anytime, anywhere.",
      },
    },
    metrics: {
      label: "Traction & Milestones",
      heading: "Validated Progress",
      sub: "Institutional backing, peer-reviewed validation, and strategic partnerships demonstrate RoLink's translational readiness.",
      cards: {
        patentsL: "Patents Filed",      patentsS: "PCT/HU2025/050010 + 1 pending",
        pubsL: "Peer-Reviewed Publications", pubsS: "Viruses, Vaccines (MDPI)",
        partL: "Institutional Partners", partS: "Defense, academic, veterinary",
        modelsL: "Validated Virus Models", modelsS: "SARS-CoV-2, Sindbis, Zika",
        grantsL: "Grants Secured",       grantsS: "Combined grant and institutional project funding.",
        bsl4L: "BSL-4 Partnership",      bsl4S: "University of Pécs NLV",
      },
    },
    applications: {
      label: "Applications",
      heading: "Platform Applications",
      apps: {
        vaccineT: "Accelerated Vaccine Candidate Development",
        vaccineD: "Structurally preserved UV-inactivated viruses support safer and faster vaccine candidate development workflows without requiring continuous high-containment laboratory access. RoLink technology enables researchers to evaluate immunogenic profiles, conduct antigen characterization, and advance preclinical candidates under standard BSL-2 conditions.",
        diagnosticsT: "Diagnostics",
        diagnosticsD: "Develop and validate diagnostic assays using structurally identical but safe virus samples. UV-inactivated materials preserve surface antigens and RNA integrity, enabling accurate ELISA, PCR, and lateral flow assay development with reduced biosafety requirements.",
        biodefenseT: "Biodefense",
        biodefenseD: "UV-inactivated viruses enable safer validation and testing environments for biodefense technologies, diagnostics, and response systems. Applications include device testing, assay validation, biodefense preparedness protocols, and safer downstream workflows for defense-critical biological threat assessment.",
        pandemicT: "Pandemic Preparedness",
        pandemicD: "Establish pre-positioned inactivation capacity for rapid response to novel pathogens. Reduce the time from pathogen identification to research-ready materials from months to weeks by eliminating the dependency on high-containment infrastructure availability.",
        fasterT: "Faster Vaccine and Assay Development",
        fasterD: "Preserved viral structure and accessible BSL-2 workflows accelerate diagnostics, assay development, and vaccine candidate evaluation. By removing the bottleneck of BSL-3/BSL-4 scheduling and access, development timelines are compressed significantly across multiple downstream applications.",
        accessT: "Opening High-Containment Research to BSL-2 Laboratories",
        accessD: "RoLink technology enables broader access to high-containment virology workflows by transforming dangerous pathogens into structurally intact, research-compatible UV-inactivated materials operable in BSL-2 environments. This fundamentally democratizes access to virology research for academic, institutional, and commercial laboratories worldwide.",
      },
    },
    biosafety: {
      label: "Biosafety Infrastructure",
      heading: "The Painful Bottleneck",
      body: "High-containment laboratory research is expensive, operationally restrictive, infrastructure-limited, and inaccessible to most institutions worldwide. RoLink technology enables safer downstream workflows beyond traditional biosafety bottlenecks.",
      levels: {
        l2Desc: "Standard laboratory equipment. Influenza, Zika, Hepatitis B.",
        l2Avail: "~10,000+ facilities",
        l3Desc: "Enhanced containment, HEPA filtration. SARS-CoV-2, mpox, West Nile.",
        l3Avail: "~1,500 facilities",
        l4Desc: "Full positive-pressure suits. Ebola, Marburg, Nipah, CCHFV.",
        l4Avail: "51 facilities",
        rolinkEnabled: "RoLink Enabled",
      },
      quotePre: "\"BSL-4 operators are rarer than astronauts — ",
      quoteAccent: "RoLink makes downstream applications available and scalable.\"",
    },
    sci: {
      label: "Scientific Validation",
      heading: "Evidence-Based Results",
      body: "RoLink technology has been validated through internal research and independent laboratory collaborations, demonstrating that UV inactivation preserves critical viral structures while enabling safer downstream applications compared to traditional chemical methods.",
      tabs: {
        comparison: "Technology Comparison",
        qpcr: "qPCR Integrity",
        elisa: "ELISA Reactivity",
        purification: "Purification",
      },
      interpretation: "Scientific Interpretation",
      fig: "Fig.",
      comparison: {
        title: "Comparison of Inactivation Technologies for Vaccine Production",
        subtitle: "Chemical inactivation vs UV inactivation",
        colFeature: "Feature",
        colChemical: "Chemical Inactivation",
        colUv: "UV Inactivation",
        rows: {
          speedF: "Production Speed",       speedC: "Days to weeks",                   speedU: "Potentially within one day",
          purifF: "Purification Integration", purifC: "Separate step required",        purifU: "Can integrate into workflow",
          costF: "Production Cost",         costC: "Higher chemical & handling costs", costU: "May reduce overall costs",
          shelfF: "Shelf Life",             shelfC: "Standard",                         shelfU: "May support longer shelf life",
          manufF: "Manufacturing",          manufC: "Batch processing",                 manufU: "Compatible with continuous-flow",
          immuneF: "Immune Response",       immuneC: "Mainly IgG1-dominant antibodies", immuneU: "Broader: T-cell + antibody responses",
        },
        caption: "Comparison of chemical and UV-based inactivation approaches for vaccine production workflows.",
        interp1: "UV inactivation offers potential advantages over traditional chemical methods across multiple production parameters. Key differences include faster processing times (potentially within one day vs days to weeks), compatibility with continuous-flow manufacturing, and the ability to integrate purification directly into the production workflow.",
        interp2: "Importantly, UV-inactivated vaccines may support broader immune activation including both T-cell and antibody responses, whereas chemically inactivated vaccines tend to primarily induce IgG1-dominant antibody responses. These characteristics may contribute to improved vaccine efficacy and broader protective immunity.",
      },
      qpcr: {
        title: "Comparison of qPCR Cq Values Between Active and UV-Inactivated SARS-CoV-2 Samples",
        meta: "Average Cq ± SE across dilutions (1x, 10x, 100x) · SARS-CoV-2 Delta · UV-C 254nm, 5–90s",
        yLabel: "Average Cq Value",
        groupActive: "Active\nSARS-CoV-2",
        groupUv: "UV-Inactivated\nSARS-CoV-2",
        legendActive: "Active SARS-CoV-2 (20.47 ± 2.51)",
        legendUv: "UV-Inactivated (20.89 ± 0.92)",
        caption: "Comparison of qPCR Cq values between active and UV-inactivated SARS-CoV-2 samples across three dilutions.",
        source: "Data from independent official laboratory testing by the National Laboratory of Virology.",
        interp1: "The bar chart displays the average quantification cycle (Cq) values ± standard error for SARS-CoV-2 Delta variant RNA across three dilutions (1x, 10x, 100x) before and after UV-C inactivation. The mean Cq for active samples (20.47 ± 2.51) and UV-inactivated samples (20.89 ± 0.92) show no statistically significant difference.",
        interp2: "These data indicate no significant degradation of viral RNA following UV-C exposure at 254 nm, across exposure durations of 5–90 seconds. The lower standard error in the UV-inactivated group further suggests consistent and reproducible preservation of RNA integrity across experimental conditions. This confirms suitability for downstream molecular diagnostics and PCR-based surveillance applications.",
      },
      elisa: {
        title: "Comparison of ELISA Reactivity Between Active and UV-Inactivated SARS-CoV-2 RBD Antigen",
        meta: "Average signal ± SE across serum dilutions (4x–32x) · p < 0.05",
        yLabel: "Average ELISA Signal",
        legendActive: "Active SARS-CoV-2",
        legendUv: "UV-Inactivated",
        caption: "Comparison of ELISA reactivity between active and UV-inactivated SARS-CoV-2 RBD antigen across serum dilutions.",
        source: "Experiments conducted by the official laboratory of the Hungarian Defence Forces.",
        interp1: "The summarized bar chart displays the average ELISA signal ± standard error for active and UV-inactivated SARS-CoV-2 RBD antigen across four serum dilutions (4x to 32x). Similar reactivity levels between active and UV-inactivated conditions indicate that UV-C inactivation at 254 nm preserves antigenic structural integrity.",
        interp2: "Statistically reliable results with p < 0.05 and minimal variability support the use of UV-inactivated virus in diagnostics, immunological studies, and vaccine development workflows. The preserved RBD antigen reactivity validates UV-inactivated viruses as safe, research-compatible reference materials that faithfully represent the immunological profile of live virus.",
      },
      purification: {
        title: "Purification of Infective Virus",
        meta: "Ion-exchange-based purification of lipid-enveloped viruses",
        body: "RoLink's purification work relates to ion-exchange-based purification processes for lipid-enveloped viruses. The method improves recovery efficiency of intact infective virions, including SARS-CoV-2, by using optimized elution conditions designed to preserve viral integrity and enhance downstream usability.",
        patentNote: "Related purification and UV-inactivation technologies are associated with ongoing patent protection efforts, including PCT/HU2025/050010.",
        targetL: "Target",
        targetV: "Lipid-enveloped viruses",
        methodL: "Method",
        methodV: "Ion-exchange chromatography",
        outcomeL: "Outcome",
        outcomeV: "Preserved viral integrity",
      },
    },
    claims: {
      heading: "Quantitative Scientific Proof",
      cards: {
        proteinL: "Intact Protein",
        proteinD: "Complete ELISA-validated preservation of antigenic structures (p<0.05)",
        pcrL: "Intact PCR",
        pcrD: "RNA integrity confirmed via qPCR (R²=0.007, p=0.367 — no degradation)",
        costL: "Biosafety Costs",
        costD: "BSL-4 to BSL-2 downgrade eliminates high-containment overhead",
        yieldL: "Virus Purification: Infectious Yield",
        yieldD: "TCID50 validated, up to 75.5% RNA recovery via purification",
        uvL: "UV-C Platform",
        uvD: "Proprietary inactivation at 254nm — patented technology",
        scaleL: "Directly Scalable Technologies",
        scaleD: "Closed-system bioreactor to lyophilization pipeline in development",
      },
    },
    investment: {
      label: "Investment Opportunity",
      heading: "Building Scalable Biodefense Infrastructure",
      sub: "RoLink is positioned at the intersection of a $21.5B CBRN market, institutional defense mandates, and a validated technology platform with clear regulatory and commercialization pathways.",
      pillars: {
        marketT: "Market Opportunity",
        marketD: "The global CBRN defense market is valued at $21.5B (2024) with European Defence Fund allocating €1B+ in 2025 calls. UV inactivation technology addresses a validated unmet need across defense, public health, and veterinary sectors.",
        ipT: "IP & Competitive Position",
        ipD: "Protected by PCT/HU2025/050010 with additional patents pending. RoLink's proprietary UV-C calibration preserves both protein and RNA integrity — a differentiation no chemical inactivation method achieves.",
        trlT: "Technology Readiness",
        trlD: "TRL 4–5 with validated preclinical data (ELISA, qPCR, animal models). Clear pathway to GMP-scale production through closed-system bioreactor integration currently in development.",
        modelT: "Business Model",
        modelD: "Technology licensing to pharmaceutical, veterinary, and defense partners. Service contracts for institutional virus inactivation. Co-development agreements for vaccine candidate programs.",
      },
      cta: "Request Investor Information",
    },
    contact: {
      label: "Get In Touch",
      heading: "Let's build the future of pandemic resilience together.",
      body: "Whether you're a government agency, research institution, defense organization, or biotech partner, we'd like to hear from you.",
      partnerships: "Partnerships",
      investors: "Investor Relations",
      general: "General Inquiries",
      orgFooter: "RoLink Biotechnology Research and Development Kft.",
      addr: "7624 Pécs, Ifjúság útja 20., Hungary",
      form: {
        name: "Full Name",
        email: "Email",
        org: "Organization",
        type: "Inquiry Type",
        typePlaceholder: "Select type",
        message: "Message",
        submit: "Submit Inquiry",
        submitting: "Submitting...",
        error: "Failed to submit. Please try again.",
        successT: "Inquiry Received",
        successB: "We'll review your message and respond within 2 business days.",
        types: {
          Partnership: "Partnership",
          Investment: "Investment",
          ResearchCollaboration: "Research Collaboration",
          GovernmentDefense: "Government/Defense",
          Media: "Media",
          General: "General",
        },
      },
    },
    footer: {
      orgLine: "RoLink Biotechnology Research and Development Kft.",
      founded: "Founded 2020 · Pécs, Hungary",
      addr: "7624 Pécs, Ifjúság útja 20.",
      groups: { platform: "Platform", company: "Company", legal: "Legal" },
      links: {
        Technology: "Technology", Applications: "Applications", Biosafety: "Biosafety", Science: "Science",
        Team: "Team", Partners: "Partners", News: "News", Contact: "Contact",
        "Privacy Policy": "Privacy Policy", "Terms of Service": "Terms of Service", "Cookie Policy": "Cookie Policy",
      },
      copy: "All rights reserved.",
      reg: "Reg. No: 02-09-085234 · Tax No: 28735557-2-02",
      photoCredits: "Photo credits",
    },
    common: {
      readMore: "Read more",
      learnMore: "Learn more",
      viewAll: "View all",
    },
  },

  hu: {
    nav: {
      home: "Főoldal",
      technology: "Technológia",
      science: "Tudomány",
      applications: "Alkalmazások",
      partners: "Partnerek",
      team: "Csapat",
      investors: "Befektetők",
      news: "Hírek",
      contact: "Kapcsolat",
      search: "Keresés",
    },
    hero: {
      badge: "Védelmi képesség a Járványok Korában",
      headlineLine1: "KIKAPCSOLT",
      headlineLine2Pre: "",
      headlineLine2Red: "VÍRUS",
      subtitle: "Gyorsreagálású UV-inaktiválás a pandémiás felkészültségért.",
      body: "A RoLink Biotechnology UV-alapú vírusinaktiválási platformokat fejleszt, amelyek biztonságosabb diagnosztikát, vakcinafejlesztést és biodefenz alkalmazásokat tesznek lehetővé a vírus szerkezetének megőrzése mellett.",
      explore: "Technológia megismerése",
      investor: "Befektetői információk",
      contact: "Kapcsolatfelvétel",
      scroll: "Görgetés",
    },
    problem: {
      badge: "A globális kihívás",
      headlinePre: "Mindössze ",
      headlineAccent: "51 BSL-4 létesítmény",
      headlineSuf: " létezik világszerte.",
      body: "A magas biztonsági szintű virológiai kutatás drága, működtetése korlátozott, és az intézmények többsége számára nem elérhető. A veszélyes kórokozók BSL-3/BSL-4 infrastruktúrát igényelnek, amelyet a laboratóriumok kevesebb mint 1%-a tud biztosítani — kritikus szűk keresztmetszetet okozva a pandémiás felkészültségben, vakcinafejlesztésben és a biodefenz kutatásban.",
      bsl: { l2: "Standard laborok", l3: "Megerősített elszigetelés", l4: "Maximális elszigetelés" },
      cta: "A RoLink megszünteti ezt a szűk keresztmetszetet",
    },
    technology: {
      label: "UV-inaktiválási platform",
      heading: "Hogyan működik",
      stages: {
        activeT: "Aktív vírus",
        activeD: "Veszélyes élő vírus, amely BSL-3/BSL-4 elszigetelést igényel. Korlátozott hozzáférés, alacsony K+F-kapacitás, rendkívül drága infrastruktúra és magasan képzett munkaerő szükséges. Mindössze 51 BSL-4 létesítmény üzemel világszerte.",
        uvT: "UV-C inaktiválás (254 nm)",
        uvD: "Szabadalmaztatott UV-C inaktiválás 254 nm-es hullámhosszon. A pontosan kalibrált expozíció (5–90 mp) megszünteti a vírus replikációs képességét, megőrizve a szerkezeti integritást.",
        inT: "UV-inaktivált vírus",
        inD: "Biztonságos, szerkezetileg ép vírus: 100%-os fehérjemegőrzés (ELISA-validált), 100%-os PCR-detektálható RNS (qPCR, R²=0,007). BSL-2 szinten kezelhető. Nem keletkezik veszélyes biológiai hulladék. Költséghatékony, bárhol, bármikor elérhető.",
      },
    },
    metrics: {
      label: "Eredmények és mérföldkövek",
      heading: "Validált eredmények",
      sub: "Intézményi támogatás, lektorált validáció és stratégiai partnerségek bizonyítják a RoLink transzlációs felkészültségét.",
      cards: {
        patentsL: "Benyújtott szabadalmak", patentsS: "PCT/HU2025/050010 + 1 folyamatban",
        pubsL: "Lektorált publikációk",     pubsS: "Viruses, Vaccines (MDPI)",
        partL: "Intézményi partnerek",      partS: "Védelmi, akadémiai, állategészségügyi",
        modelsL: "Validált vírusmodellek",  modelsS: "SARS-CoV-2, Sindbis, Zika",
        grantsL: "Elnyert pályázati források", grantsS: "Pályázati és intézményi projektforrások együttesen.",
        bsl4L: "BSL-4 partnerség",          bsl4S: "Pécsi Tudományegyetem VNL",
      },
    },
    applications: {
      label: "Alkalmazások",
      heading: "Platform alkalmazások",
      apps: {
        vaccineT: "Gyorsított vakcinajelölt-fejlesztés",
        vaccineD: "A szerkezetileg megőrzött, UV-inaktivált vírusok biztonságosabb és gyorsabb vakcinajelölt-fejlesztési munkafolyamatokat tesznek lehetővé anélkül, hogy folyamatos magas biztonsági szintű laboratóriumi hozzáférésre lenne szükség. A RoLink technológiája lehetővé teszi a kutatók számára az immunogenitási profilok értékelését, az antigének jellemzését és a preklinikai jelöltek fejlesztését standard BSL-2 körülmények között.",
        diagnosticsT: "Diagnosztika",
        diagnosticsD: "Diagnosztikai tesztek fejlesztése és validálása szerkezetileg azonos, mégis biztonságos vírusminták felhasználásával. Az UV-inaktivált anyagok megőrzik a felszíni antigéneket és az RNS integritását, lehetővé téve a pontos ELISA-, PCR- és lateral flow tesztek fejlesztését csökkentett biológiai biztonsági követelmények mellett.",
        biodefenseT: "Biodefenz",
        biodefenseD: "Az UV-inaktivált vírusok biztonságosabb validálási és tesztelési környezetet biztosítanak a biodefenz technológiák, diagnosztikai és reagáló rendszerek számára. Alkalmazási területei közé tartozik az eszköztesztelés, a tesztvalidálás, a biodefenz felkészültségi protokollok és a védelmi szempontból kritikus biológiai fenyegetésértékelés biztonságosabb downstream munkafolyamatai.",
        pandemicT: "Pandémiás felkészültség",
        pandemicD: "Előre telepített inaktiválási kapacitás kialakítása az új kórokozókra adott gyors válaszhoz. A kórokozó azonosításától a kutatásra kész anyagokig eltelt idő hónapokról hetekre csökkenthető a magas biztonsági szintű infrastruktúra rendelkezésre állásától való függőség megszüntetésével.",
        fasterT: "Gyorsabb vakcina- és teszt-fejlesztés",
        fasterD: "A megőrzött vírusszerkezet és az elérhető BSL-2 munkafolyamatok felgyorsítják a diagnosztikai tesztek, az assay-fejlesztés és a vakcinajelöltek értékelését. A BSL-3/BSL-4 időbeosztás és hozzáférés szűk keresztmetszetének felszámolásával a fejlesztési idők jelentősen lerövidülnek számos downstream alkalmazásban.",
        accessT: "A magas biztonsági szintű kutatás megnyitása a BSL-2 laboratóriumok számára",
        accessD: "A RoLink technológiája szélesebb körű hozzáférést tesz lehetővé a magas biztonsági szintű virológiai munkafolyamatokhoz azáltal, hogy a veszélyes kórokozókat szerkezetileg ép, kutatáshoz alkalmas, BSL-2 környezetben kezelhető UV-inaktivált anyagokká alakítja. Ez alapjaiban demokratizálja a virológiai kutatáshoz való hozzáférést az akadémiai, intézményi és kereskedelmi laboratóriumok számára világszerte.",
      },
    },
    biosafety: {
      label: "Biológiai biztonsági infrastruktúra",
      heading: "A kritikus szűk keresztmetszet",
      body: "A magas biztonsági szintű laboratóriumi kutatás drága, működésileg korlátozott, infrastruktúrában szűkös, és a világ legtöbb intézménye számára nem elérhető. A RoLink technológiája biztonságosabb downstream munkafolyamatokat tesz lehetővé a hagyományos biológiai biztonsági szűk keresztmetszeteken túl.",
      levels: {
        l2Desc: "Standard laboratóriumi felszerelés. Influenza, Zika, hepatitis B.",
        l2Avail: "~10 000+ létesítmény",
        l3Desc: "Megerősített elszigetelés, HEPA-szűrés. SARS-CoV-2, majomhimlő, nyugat-nílusi vírus.",
        l3Avail: "~1 500 létesítmény",
        l4Desc: "Teljes túlnyomásos védőruhák. Ebola, Marburg, Nipah, CCHFV.",
        l4Avail: "51 létesítmény",
        rolinkEnabled: "RoLink-kompatibilis",
      },
      quotePre: "„A BSL-4 operátorok ritkábbak az űrhajósoknál — ",
      quoteAccent: "a RoLink elérhetővé és skálázhatóvá teszi a downstream alkalmazásokat.”",
    },
    sci: {
      label: "Tudományos validáció",
      heading: "Bizonyíték-alapú eredmények",
      body: "A RoLink technológiát belső kutatások és független laboratóriumi együttműködések validálták, igazolva, hogy az UV-inaktiválás megőrzi a kritikus vírusszerkezeteket, miközben biztonságosabb downstream alkalmazásokat tesz lehetővé a hagyományos kémiai módszerekhez képest.",
      tabs: {
        comparison: "Technológiai összehasonlítás",
        qpcr: "qPCR-integritás",
        elisa: "ELISA-reaktivitás",
        purification: "Tisztítás",
      },
      interpretation: "Tudományos értelmezés",
      fig: "Ábra",
      comparison: {
        title: "Inaktiválási technológiák összehasonlítása vakcinagyártásban",
        subtitle: "Kémiai inaktiválás vs. UV-inaktiválás",
        colFeature: "Jellemző",
        colChemical: "Kémiai inaktiválás",
        colUv: "UV-inaktiválás",
        rows: {
          speedF: "Gyártási sebesség",          speedC: "Napok–hetek",                              speedU: "Akár egy napon belül",
          purifF: "Tisztítás integrációja",     purifC: "Külön lépés szükséges",                    purifU: "Beépíthető a munkafolyamatba",
          costF: "Gyártási költség",            costC: "Magasabb vegyszer- és kezelési költségek",  costU: "Csökkentheti az összköltséget",
          shelfF: "Eltarthatóság",              shelfC: "Standard",                                  shelfU: "Hosszabb eltarthatóságot támogathat",
          manufF: "Gyártás",                    manufC: "Tételes feldolgozás",                       manufU: "Folyamatos áramlású gyártással kompatibilis",
          immuneF: "Immunválasz",               immuneC: "Túlnyomórészt IgG1-dominált antitestek",   immuneU: "Szélesebb körű: T-sejtes + antitestválasz",
        },
        caption: "Kémiai és UV-alapú inaktiválási megközelítések összehasonlítása vakcinagyártási munkafolyamatokban.",
        interp1: "Az UV-inaktiválás potenciális előnyöket kínál a hagyományos kémiai módszerekkel szemben több gyártási paraméter mentén. A legfőbb különbségek közé tartozik a gyorsabb feldolgozási idő (akár egy napon belül a napok–hetek helyett), a folyamatos áramlású gyártással való kompatibilitás, valamint az a képesség, hogy a tisztítás közvetlenül beépíthető a gyártási folyamatba.",
        interp2: "Fontos, hogy az UV-inaktivált vakcinák szélesebb körű immunaktivációt — beleértve mind a T-sejtes, mind az antitest-válaszokat — támogathatnak, míg a kémiailag inaktivált vakcinák jellemzően elsősorban IgG1-dominált antitestválaszt váltanak ki. Ezek a tulajdonságok hozzájárulhatnak a vakcinák hatékonyságának javításához és a szélesebb védő immunitás kialakításához.",
      },
      qpcr: {
        title: "qPCR Cq-értékek összehasonlítása aktív és UV-inaktivált SARS-CoV-2 minták között",
        meta: "Átlagos Cq ± SE három hígításban (1×, 10×, 100×) · SARS-CoV-2 Delta · UV-C 254 nm, 5–90 mp",
        yLabel: "Átlagos Cq-érték",
        groupActive: "Aktív\nSARS-CoV-2",
        groupUv: "UV-inaktivált\nSARS-CoV-2",
        legendActive: "Aktív SARS-CoV-2 (20,47 ± 2,51)",
        legendUv: "UV-inaktivált (20,89 ± 0,92)",
        caption: "qPCR Cq-értékek összehasonlítása aktív és UV-inaktivált SARS-CoV-2 minták között három hígításban.",
        source: "Független hivatalos laboratóriumi tesztelés a Virológiai Nemzeti Laboratórium által.",
        interp1: "Az oszlopdiagram az átlagos kvantifikációs ciklus (Cq) értékeket mutatja ± standard hiba mellett a SARS-CoV-2 Delta variáns RNS-ére három hígításban (1×, 10×, 100×) az UV-C inaktiválás előtt és után. Az aktív minták (20,47 ± 2,51) és az UV-inaktivált minták (20,89 ± 0,92) átlagos Cq-értéke között nincs statisztikailag szignifikáns különbség.",
        interp2: "Ezek az adatok azt mutatják, hogy 254 nm-es UV-C expozíció után — 5–90 másodperces időtartamokban — nem következik be a vírus RNS jelentős degradációja. Az UV-inaktivált csoport alacsonyabb standard hibája emellett az RNS-integritás következetes és reprodukálható megőrzésére utal a kísérleti körülmények között. Ez igazolja az alkalmasságot a downstream molekuláris diagnosztikai és PCR-alapú surveillance alkalmazásokhoz.",
      },
      elisa: {
        title: "ELISA-reaktivitás összehasonlítása aktív és UV-inaktivált SARS-CoV-2 RBD-antigén között",
        meta: "Átlagos jel ± SE szérumhígításokban (4×–32×) · p < 0,05",
        yLabel: "Átlagos ELISA-jel",
        legendActive: "Aktív SARS-CoV-2",
        legendUv: "UV-inaktivált",
        caption: "ELISA-reaktivitás összehasonlítása aktív és UV-inaktivált SARS-CoV-2 RBD-antigén között szérumhígításokban.",
        source: "A kísérleteket a Magyar Honvédség hivatalos laboratóriuma végezte.",
        interp1: "Az összefoglaló oszlopdiagram az aktív és UV-inaktivált SARS-CoV-2 RBD-antigén átlagos ELISA-jelét mutatja ± standard hiba mellett négy szérumhígításban (4× és 32× között). Az aktív és UV-inaktivált körülmények közötti hasonló reaktivitási szintek azt jelzik, hogy a 254 nm-es UV-C inaktiválás megőrzi az antigén szerkezeti integritását.",
        interp2: "A statisztikailag megbízható, p < 0,05 melletti eredmények és a minimális variabilitás alátámasztják az UV-inaktivált vírusok alkalmazását a diagnosztikában, az immunológiai vizsgálatokban és a vakcinafejlesztési munkafolyamatokban. A megőrzött RBD-antigén reaktivitása igazolja, hogy az UV-inaktivált vírusok biztonságos, kutatáshoz alkalmas referenciaanyagok, amelyek hűen tükrözik az élő vírus immunológiai profilját.",
      },
      purification: {
        title: "Fertőző vírus tisztítása",
        meta: "Lipidburkos vírusok ioncserés tisztítása",
        body: "A RoLink tisztítási munkája a lipidburkos vírusok ioncserés tisztítási folyamataival foglalkozik. A módszer az ép, fertőzőképes virionok — köztük a SARS-CoV-2 — visszanyerési hatékonyságát javítja optimalizált eluciós körülmények alkalmazásával, amelyek a vírusintegritás megőrzésére és a downstream felhasználhatóság növelésére irányulnak.",
        patentNote: "A kapcsolódó tisztítási és UV-inaktiválási technológiák folyamatban lévő szabadalmi védelem alatt állnak, beleértve a PCT/HU2025/050010 számú bejelentést.",
        targetL: "Célpont",
        targetV: "Lipidburkos vírusok",
        methodL: "Módszer",
        methodV: "Ioncserés kromatográfia",
        outcomeL: "Eredmény",
        outcomeV: "Megőrzött vírusintegritás",
      },
    },
    claims: {
      heading: "Mennyiségi tudományos bizonyíték",
      cards: {
        proteinL: "Ép fehérje",
        proteinD: "Teljes, ELISA-val validált megőrzés az antigén szerkezetében (p < 0,05)",
        pcrL: "Ép PCR",
        pcrD: "Az RNS-integritás qPCR-rel megerősítve (R²=0,007, p=0,367 — nincs degradáció)",
        costL: "Biológiai biztonsági költségek",
        costD: "A BSL-4-ről BSL-2-re történő visszaminősítés megszünteti a magas elszigetelés többletköltségét",
        yieldL: "Vírustisztítás: fertőzőképes hozam",
        yieldD: "TCID50-validált, akár 75,5%-os RNS-visszanyerés tisztítással",
        uvL: "UV-C platform",
        uvD: "Szabadalmaztatott inaktiválás 254 nm-en — szabadalommal védett technológia",
        scaleL: "Közvetlenül skálázható technológiák",
        scaleD: "Zárt rendszerű bioreaktortól a liofilizálási folyamatig — fejlesztés alatt",
      },
    },
    investment: {
      label: "Befektetési lehetőség",
      heading: "Skálázható biodefenz infrastruktúra építése",
      sub: "A RoLink a 21,5 milliárd dolláros CBRN-piac, az intézményi védelmi mandátumok és egy validált, világos szabályozási és kereskedelmi útvonallal rendelkező technológiai platform metszéspontjában helyezkedik el.",
      pillars: {
        marketT: "Piaci lehetőség",
        marketD: "A globális CBRN-védelmi piac értéke 21,5 milliárd dollár (2024), miközben az Európai Védelmi Alap több mint 1 milliárd eurós forrást irányoz elő 2025-ös pályázataira. Az UV-inaktiválási technológia validált, kielégítetlen igényt szolgál ki a védelem, a közegészségügy és az állatorvostudomány területén.",
        ipT: "Szellemi tulajdon és versenypozíció",
        ipD: "PCT/HU2025/050010 számú bejelentéssel védve, további szabadalmak folyamatban. A RoLink szabadalmaztatott UV-C kalibrálása mind a fehérje-, mind az RNS-integritást megőrzi — ezt a megkülönböztetést egyetlen kémiai inaktiválási módszer sem éri el.",
        trlT: "Technológiai érettség",
        trlD: "TRL 4–5 szint, validált preklinikai adatokkal (ELISA, qPCR, állatmodellek). Világos útvonal a GMP-szintű gyártáshoz a jelenleg fejlesztés alatt álló zárt rendszerű bioreaktor-integráción keresztül.",
        modelT: "Üzleti modell",
        modelD: "Technológialicenc gyógyszeripari, állategészségügyi és védelmi partnerek számára. Szolgáltatási szerződések intézményi vírusinaktiválásra. Közös fejlesztési megállapodások vakcinajelölt-programokra.",
      },
      cta: "Befektetői információk kérése",
    },
    contact: {
      label: "Lépjen velünk kapcsolatba",
      heading: "Építsük ki együtt az európai szintű védelmi képességet a járványok korában.",
      body: "Akár kormányzati ügynökség, kutatóintézet, védelmi szervezet vagy biotechnológiai partner — szívesen halljuk Önt.",
      partnerships: "Partnerségek",
      investors: "Befektetői kapcsolatok",
      general: "Általános megkeresések",
      orgFooter: "RoLink Biotechnology Research and Development Kft.",
      addr: "7624 Pécs, Ifjúság útja 20., Magyarország",
      form: {
        name: "Teljes név",
        email: "E-mail",
        org: "Szervezet",
        type: "Megkeresés típusa",
        typePlaceholder: "Válasszon típust",
        message: "Üzenet",
        submit: "Megkeresés elküldése",
        submitting: "Küldés folyamatban...",
        error: "A küldés sikertelen. Kérjük, próbálja újra.",
        successT: "Megkeresés megérkezett",
        successB: "Üzenetét 2 munkanapon belül feldolgozzuk és válaszolunk.",
        types: {
          Partnership: "Partnerség",
          Investment: "Befektetés",
          ResearchCollaboration: "Kutatási együttműködés",
          GovernmentDefense: "Kormányzat / Védelem",
          Media: "Sajtó",
          General: "Általános",
        },
      },
    },
    footer: {
      orgLine: "RoLink Biotechnology Research and Development Kft.",
      founded: "Alapítva: 2020 · Pécs, Magyarország",
      addr: "7624 Pécs, Ifjúság útja 20.",
      groups: { platform: "Platform", company: "Vállalat", legal: "Jogi" },
      links: {
        Technology: "Technológia", Applications: "Alkalmazások", Biosafety: "Biológiai biztonság", Science: "Tudomány",
        Team: "Csapat", Partners: "Partnerek", News: "Hírek", Contact: "Kapcsolat",
        "Privacy Policy": "Adatvédelmi szabályzat", "Terms of Service": "Felhasználási feltételek", "Cookie Policy": "Süti-szabályzat",
      },
      copy: "Minden jog fenntartva.",
      reg: "Cégjegyzékszám: 02-09-085234 · Adószám: 28735557-2-02",
      photoCredits: "Fotók",
    },
    common: {
      readMore: "Tovább",
      learnMore: "Tudjon meg többet",
      viewAll: "Összes megtekintése",
    },
  },
};
